import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, MessageCircle, Mail, RefreshCw, History } from 'lucide-react';
import { PresetGroup, StatusPill, EmptyState, TableRowsSkeleton } from '../ds';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHANNELS = [{ key: 'all', label: 'All' }, { key: 'whatsapp', label: 'WhatsApp' }, { key: 'email', label: 'Email' }];
const fmtSent = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export const AutomationLogModal = ({ eventType, onClose }) => {
  const [channel, setChannel] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 150 });
    if (channel !== 'all') params.set('channel', channel);
    axios.get(`${API_URL}/api/calendar/event-types/${eventType.event_type_id}/automation-log?${params}`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} })).finally(() => setLoading(false));
  };
  useEffect(load, [channel, eventType.event_type_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = data?.items || [];
  const counts = data?.counts || {};
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="automation-log-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-900">Automation log</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{eventType.name} · WhatsApp &amp; email reminders actually sent (newest first)</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="automation-log-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-slate-100 flex-wrap">
          <PresetGroup options={CHANNELS} value={channel} onChange={setChannel} testIdPrefix="automation-log-channel" />
          <div className="flex items-center gap-3 text-xs text-slate-600" data-testid="automation-log-counts">
            <span><b className="text-slate-900">{counts.total ?? 0}</b> sent</span>
            <span className="inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /> {counts.whatsapp ?? 0}</span>
            <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-sky-600" /> {counts.email ?? 0}</span>
            {counts.failed > 0 && <span className="text-rose-700 font-medium" data-testid="automation-log-failed">{counts.failed} failed</span>}
            <button onClick={load} className="p-1.5 rounded-md hover:bg-slate-100" title="Refresh" data-testid="automation-log-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          {loading && !data ? <div className="p-6"><TableRowsSkeleton rows={5} cols={6} /></div> : items.length === 0 ? (
            <div className="p-8">
              <EmptyState icon={History} title="Nothing sent yet" hint="Reminders appear here as soon as the scheduler or a booking sends them." testId="automation-log-empty" />
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="automation-log-table">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-6 py-2.5">Sent</th><th className="text-left px-3 py-2.5">Channel</th>
                  <th className="text-left px-3 py-2.5">Trigger</th><th className="text-left px-3 py-2.5">Customer</th>
                  <th className="text-left px-3 py-2.5">Slot</th><th className="text-left px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.booking_id}-${it.channel}-${it.trigger}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid="automation-log-row">
                    <td className="px-6 py-2.5 text-slate-700 whitespace-nowrap">{fmtSent(it.sent_at)}</td>
                    <td className="px-3 py-2.5">
                      {it.channel === 'whatsapp' ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-sky-700 text-xs font-medium"><Mail className="w-3.5 h-3.5" /> {it.channel === 'email' ? 'Email' : 'Email + WhatsApp'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-slate-800 text-xs">{it.trigger_label}</td>
                    <td className="px-3 py-2.5">
                      <div className="text-slate-800 text-xs font-medium">{it.customer_name || '—'}</div>
                      <div className="text-[11px] text-slate-500">{it.channel === 'whatsapp' ? it.customer_phone : it.customer_email}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{it.booking_date} {it.booking_time}</td>
                    <td className="px-3 py-2.5">
                      <span title={it.detail}><StatusPill status={it.status === 'sent' ? 'ok' : it.status === 'skipped' ? 'new' : it.status === 'partial' ? 'pending' : 'failed'} label={it.status} /></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
